import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

const TEMPLATES = [
  { id: "luxury-black-tie", name: "Luxury Black Tie", img: "https://images.pexels.com/photos/33403878/pexels-photo-33403878.jpeg?w=600" },
  { id: "modern-minimal", name: "Modern Minimal", img: "https://images.unsplash.com/photo-1718997534125-052b2ff48cd0?w=600" },
  { id: "romantic-garden", name: "Romantic Garden", img: "https://images.pexels.com/photos/35985203/pexels-photo-35985203.jpeg?w=600" },
  { id: "royal-luxury", name: "Royal Luxury", img: "https://images.pexels.com/photos/11296094/pexels-photo-11296094.jpeg?w=600" },
];

export default function Onboarding() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(1);
  const [partnerOne, setPartnerOne] = useState("");
  const [partnerTwo, setPartnerTwo] = useState("");
  const [date, setDate] = useState("");
  const [venue, setVenue] = useState("");
  const [city, setCity] = useState("");
  const [template, setTemplate] = useState("luxury-black-tie");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/weddings/mine").then(() => nav("/dashboard", { replace: true })).catch(() => {}); }, [nav]);

  async function finish() {
    setBusy(true);
    try {
      const couple_names = `${partnerOne} & ${partnerTwo}`;
      const slug = `${partnerOne}-and-${partnerTwo}`.toLowerCase().replace(/[^a-z0-9]+/g, "-");
      await api.post("/weddings", {
        couple_names,
        partner_one: partnerOne,
        partner_two: partnerTwo,
        wedding_date: date || null,
        venue,
        city,
        slug,
        template,
      });
      await refresh();
      toast.success("Your wedding site is live!");
      nav("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-[var(--bg)] flex items-center justify-center px-6 py-16">
      <div className="max-w-2xl w-full">
        <div className="flex items-center justify-center gap-2 mb-12">
          {[1, 2, 3].map((s) => (
            <div key={s} className={`h-1 w-12 rounded-full ${step >= s ? "bg-[var(--gold)]" : "bg-[var(--line)]"}`} />
          ))}
        </div>

        {step === 1 && (
          <div className="text-center">
            <p className="font-script text-[var(--gold)] text-3xl mb-2">Let's begin</p>
            <h1 className="font-serif font-light text-4xl md:text-5xl mb-2">Who is getting married?</h1>
            <p className="text-[var(--muted)] mb-12">We'll use these to personalize your wedding site.</p>

            <div className="grid sm:grid-cols-2 gap-6 mb-10 text-left max-w-lg mx-auto">
              <div>
                <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Partner One</label>
                <input data-testid="onb-partner-one" value={partnerOne} onChange={(e) => setPartnerOne(e.target.value)} className="input-luxe" placeholder="Shawn" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Partner Two</label>
                <input data-testid="onb-partner-two" value={partnerTwo} onChange={(e) => setPartnerTwo(e.target.value)} className="input-luxe" placeholder="Brittney" />
              </div>
            </div>

            {partnerOne && partnerTwo && (
              <p className="font-script text-[var(--gold)] text-5xl mb-10">{partnerOne} & {partnerTwo}</p>
            )}

            <button data-testid="onb-step1-next" disabled={!partnerOne || !partnerTwo} onClick={() => setStep(2)} className="btn-gold disabled:opacity-50">Continue</button>
          </div>
        )}

        {step === 2 && (
          <div className="text-center">
            <h1 className="font-serif font-light text-4xl md:text-5xl mb-2">When & Where</h1>
            <p className="text-[var(--muted)] mb-12">Set your date and venue. (You can change these any time.)</p>
            <div className="space-y-6 max-w-lg mx-auto text-left">
              <div>
                <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Wedding date</label>
                <input data-testid="onb-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input-luxe" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Venue (optional)</label>
                <input data-testid="onb-venue" value={venue} onChange={(e) => setVenue(e.target.value)} className="input-luxe" placeholder="The Estate at Peachtree" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">City (optional)</label>
                <input data-testid="onb-city" value={city} onChange={(e) => setCity(e.target.value)} className="input-luxe" placeholder="Atlanta, GA" />
              </div>
            </div>
            <div className="flex gap-3 justify-center mt-10">
              <button onClick={() => setStep(1)} className="btn-ghost">Back</button>
              <button data-testid="onb-step2-next" onClick={() => setStep(3)} className="btn-gold">Continue</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <div className="text-center">
              <h1 className="font-serif font-light text-4xl md:text-5xl mb-2">Choose your style</h1>
              <p className="text-[var(--muted)] mb-12">Pick a template — you can switch anytime.</p>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-10">
              {TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  data-testid={`tpl-${t.id}`}
                  onClick={() => setTemplate(t.id)}
                  className={`group relative overflow-hidden rounded-2xl text-left ${template === t.id ? "ring-2 ring-[var(--gold)]" : ""}`}
                >
                  <img src={t.img} className="w-full h-48 object-cover" alt={t.name} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <p className="absolute bottom-3 left-4 text-white font-serif text-lg">{t.name}</p>
                </button>
              ))}
            </div>
            <div className="flex gap-3 justify-center">
              <button onClick={() => setStep(2)} className="btn-ghost">Back</button>
              <button data-testid="onb-finish" disabled={busy} onClick={finish} className="btn-gold">
                {busy ? "Creating…" : "Create Wedding Site"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
